import { useCallback, useMemo } from "react"
import type { FC } from "react"

import type { EntityType } from "types/common.types"
import type { CsvDateRange } from "types/csv.types"
import type { IDateRange } from "types/date.types"
import useSelectOptions from "helpers/hooks/useSelectOptions"
import DateRangePopover from "widgets/dates/DateRangePopover"
import { getRangeFromReference } from "widgets/dates/helpers"

interface CsvDatesPopoverProps {
  value: CsvDateRange
  onChange: (value: CsvDateRange) => void
  entity: EntityType
  maxRangeMonths: number
  anchorEl: HTMLElement | null
  open: boolean
  handleClose: () => void
}

const CsvDatesPopover: FC<CsvDatesPopoverProps> = ({
  value,
  onChange,
  entity,
  maxRangeMonths,
  anchorEl,
  open,
  handleClose,
}) => {
  const { getSelectOptionsByEntity, getDateReferencesByEntity } = useSelectOptions()

  const selectOptions = useMemo(
    () => getSelectOptionsByEntity(entity),
    [entity, getSelectOptionsByEntity],
  )
  const dateReferences = useMemo(
    () => getDateReferencesByEntity(entity),
    [entity, getDateReferencesByEntity],
  )
  // Any selection made here replaces the snapshot taken on open, so the zoom
  // inherited from the dashboard no longer applies.
  const handlePreset = useCallback(
    (reference: string) => {
      const ref = dateReferences[reference]
      if (!ref) return
      const { fromDate, toDate } = getRangeFromReference(ref)
      onChange({ range: reference, fromDate, toDate, zoom: false })
    },
    [dateReferences, onChange],
  )

  const handleApply = useCallback(
    ({ fromDate, toDate }: IDateRange) =>
      onChange({ range: "custom", fromDate, toDate, zoom: false }),
    [onChange],
  )

  return (
    <DateRangePopover
      aria-label="csv dates popover"
      id="csv-dates-popover"
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      // The trigger sits on the left edge of the drawer.
      referencesSide="left"
      options={selectOptions}
      range={value.range}
      value={value}
      entity={entity}
      maxRangeMonths={maxRangeMonths}
      onSelectReference={handlePreset}
      onApply={handleApply}
    />
  )
}

export default CsvDatesPopover
